import { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  ArrowUpDown,
  Trash2,
  FolderInput,
  Tag,
  Mail,
  MailOpen,
  CheckSquare,
  Square,
  ChevronDown,
  Menu,
  X,
  RefreshCw,
} from "lucide-react";
import { useEmailStore } from "../../stores/emailStore";
import { useFolderStore } from "../../stores/folderStore";
import { useLabelStore } from "../../stores/labelStore";
import { useApi } from "../../hooks/useApi";
import { useAuthStore } from "../../stores/authStore";
import { refreshFolders } from "../../utils/refreshFolders";
import { saveEmailsToCache, getCachedEmailsByFolder } from "../../utils/offlineStorage";
import { deleteEmailsWithUndo } from "../../utils/deleteWithUndo";
import { showToast } from "../../stores/toastStore";
import EmailListItem from "./EmailListItem";
import EmailSpinner from "./EmailSpinner";

interface EmailListPanelProps {
  onOpenFolderDrawer?: () => void;
  onOpenDraft?: (email: any) => void;
}

export default function EmailListPanel({ onOpenFolderDrawer, onOpenDraft }: EmailListPanelProps = {}) {  const {
    emails,
    currentEmail,
    selectedEmailIds,
    isLoading,
    error,
    searchQuery,
    sortBy,
    sortOrder,
    hasMore,
    refreshTrigger,
    setEmails,
    appendEmails,
    setLoading,
    setError,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    toggleSelectEmail,
    selectAllEmails,
    clearSelection,
    removeEmails,
    markAsRead,
    markAsUnread,
  } = useEmailStore();
  const { activeFolderId, folders } = useFolderStore();
  const { activeLabelId, labels } = useLabelStore();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showBatchMoveMenu, setShowBatchMoveMenu] = useState(false);
  const [showBatchLabelMenu, setShowBatchLabelMenu] = useState(false);

  // Pull-to-refresh state for mobile devices
  const [pullDistance, setPullDistance] = useState(0);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const touchPullStartRef = useRef<{ x: number; y: number } | null>(null);
  const isPullingRef = useRef(false);

  const pageRef = useRef(1);
  const listRef = useRef<HTMLDivElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const batchMoveRef = useRef<HTMLDivElement>(null);
  const batchLabelRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0); // Prevents stale overwrites from concurrent fetches
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showSortMenu && !showBatchMoveMenu && !showBatchLabelMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) setShowSortMenu(false);
      if (batchMoveRef.current && !batchMoveRef.current.contains(e.target as Node)) setShowBatchMoveMenu(false);
      if (batchLabelRef.current && !batchLabelRef.current.contains(e.target as Node)) setShowBatchLabelMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSortMenu, showBatchMoveMenu, showBatchLabelMenu]);

  const fetchEmails = useCallback(
    async (pageNum: number, append = false, silent = false) => {
      if (!silent) setLoading(true);
      setError(null);

      // Increment fetch ID to track this request (prevents stale overwrites)
      const thisFetchId = ++fetchIdRef.current;

      try {
        const params: Record<string, string> = {
          page: String(pageNum),
          limit: "50",
          sort: sortBy,
          order: sortOrder,
        };
        if (activeFolderId) params.folder = activeFolderId;
        if (activeLabelId) params.label = activeLabelId;
        if (searchQuery) params.q = searchQuery;

        const raw = await apiRef.current.get<{
          emails: any[];
          total: number;
          hasMore: boolean;
        }>("/mail/mails", params);

        // If a newer fetch was started, discard this stale response
        if (thisFetchId !== fetchIdRef.current) return;

        const mapped = raw.emails.map((e: any) => ({
          id: String(e.uid),
          folderId: activeFolderId || "INBOX",
          from: Array.isArray(e.from)
            ? { name: e.from[0]?.name || "", email: e.from[0]?.address || "" }
            : { name: e.from?.name || "", email: e.from?.email || "" },
          to: Array.isArray(e.to)
            ? e.to.map((t: any) => ({ name: t.name || "", email: t.address || "" }))
            : [],
          cc: Array.isArray(e.cc)
            ? e.cc.map((c: any) => ({ name: c.name || "", email: c.address || "" }))
            : undefined,
          subject: e.subject || "",
          snippet: e.snippet || "",
          body: e.body || "",
          bodyHtml: e.bodyHtml || undefined,
          date: e.date || new Date().toISOString(),
          isRead: !!e.isRead,
          isFlagged: !!e.isFlagged,
          labels: Array.isArray(e.labels) ? e.labels : [],
          size: typeof e.size === "number" ? e.size : 0,
          hasAttachments: !!e.hasAttachments,
          attachments: e.hasAttachments ? [{ filename: 'adjunto', contentType: 'application/octet-stream', size: 0, url: '' }] : undefined,
          headers: undefined,
        }));

        if (append) {
          appendEmails(mapped);
        } else {
          setEmails(mapped);
        }
        useEmailStore.getState().setTotalCount(raw.total);
        useEmailStore.getState().setHasMore(raw.hasMore);

        // Guardar en caché IndexedDB para acceso offline
        if (activeFolderId && !searchQuery) {
          saveEmailsToCache(activeFolderId, mapped);
        }
      } catch (err) {
        // If a newer fetch was started, discard this stale error
        if (thisFetchId !== fetchIdRef.current) return;

        // Fallback a IndexedDB en caso de desconexión o fallo de red
        if (activeFolderId && !searchQuery) {
          try {
            const cached = await getCachedEmailsByFolder(activeFolderId);
            if (cached && cached.length > 0) {
              const mappedCached = cached.map((e) => ({
                id: e.uid,
                folderId: e.folderId,
                from: e.from,
                to: e.to,
                cc: e.cc,
                bcc: e.bcc,
                subject: e.subject,
                snippet: e.snippet || "",
                body: e.body || "",
                bodyHtml: e.bodyHtml,
                date: e.date,
                isRead: !!e.isRead,
                isFlagged: !!e.isFlagged,
                labels: e.labels || [],
                attachments: e.attachments,
                headers: e.headers,
              }));
              setEmails(mappedCached);
              useEmailStore.getState().setTotalCount(mappedCached.length);
              useEmailStore.getState().setHasMore(false);
              if (!silent) setError(null);
              return;
            }
          } catch {
            // silent
          }
        }

        if (!silent) {
          setError(
            err instanceof Error ? err.message : "Error al cargar correos",
          );
        }
      } finally {
        if (!silent && thisFetchId === fetchIdRef.current) setLoading(false);
      }
    },
    [activeFolderId, activeLabelId, sortBy, sortOrder, searchQuery, refreshTrigger],
  );

  // Retry logic: if initial fetch fails, retry up to 3 times with exponential backoff
  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) return; // Already scheduled
    const attemptRef = { count: 0 };
    const maxRetries = 3;
    const tryRetry = () => {
      if (attemptRef.count >= maxRetries) return;
      attemptRef.count++;
      const delay = Math.min(2000 * Math.pow(2, attemptRef.count - 1), 8000);
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        // Only retry if email list is empty (no data loaded)
        if (useEmailStore.getState().emails.length === 0) {
          fetchEmails(1, false, false);
          if (useEmailStore.getState().emails.length === 0) {
            tryRetry();
          }
        }
      }, delay);
    };
    tryRetry();
  }, [fetchEmails]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    pageRef.current = 1;
    fetchEmails(1).then(() => {
      // If the list is still empty after fetch, schedule retries
      if (useEmailStore.getState().emails.length === 0) {
        scheduleRetry();
      }
    });

    // Auto-refresh silencioso de la bandeja cada 15s si estamos en la página 1
    const autoRefreshInterval = setInterval(() => {
      if (pageRef.current === 1 && !searchQuery) {
        fetchEmails(1, false, true);
      }
    }, 15000);

    return () => {
      clearInterval(autoRefreshInterval);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    };
  }, [fetchEmails, searchQuery, scheduleRetry]);

  // Refresh when browser tab becomes visible again (user returns from another tab/app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && pageRef.current === 1) {
        fetchEmails(1, false, true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchEmails]);

  // Hint de primer uso para gestos touch (swipe) — solo en dispositivos táctiles
  useEffect(() => {
    const isTouch = window.matchMedia("(hover: none)").matches || "ontouchstart" in window;
    if (!isTouch) return;
    try {
      if (localStorage.getItem("magicmail-swipe-hint-seen")) return;
      localStorage.setItem("magicmail-swipe-hint-seen", "1");
      const t = setTimeout(() => {
        showToast("Desliza un correo", {
          description: "Izquierda para eliminar · Derecha para leído/no leído",
          duration: 6000,
        });
      }, 1200);
      return () => clearTimeout(t);
    } catch {
      // localStorage no disponible; no mostrar hint
    }
  }, []);


  const handleScroll = () => {
    if (!listRef.current || !hasMore || isLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = pageRef.current + 1;
      pageRef.current = nextPage;
      fetchEmails(nextPage, true);
    }
  };

  // Pull-to-refresh touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only allow pull-to-refresh when list is at the absolute top (scrollTop === 0)
    if (!listRef.current || listRef.current.scrollTop > 0 || isPullRefreshing) {
      touchPullStartRef.current = null;
      isPullingRef.current = false;
      return;
    }
    touchPullStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    isPullingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchPullStartRef.current || isPullRefreshing) return;
    if (!listRef.current || listRef.current.scrollTop > 0) {
      if (pullDistance > 0) setPullDistance(0);
      isPullingRef.current = false;
      return;
    }

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffY = currentY - touchPullStartRef.current.y;
    const diffX = currentX - touchPullStartRef.current.x;

    // Only engage if movement is predominantly downwards (vertical)
    if (diffY > 10 && diffY > Math.abs(diffX) * 1.2) {
      isPullingRef.current = true;
      // Logarithmic / dampened resistance curve up to 80px
      const dampedDistance = Math.min(80, Math.pow(diffY, 0.85));
      setPullDistance(dampedDistance);
    } else if (diffY <= 0) {
      setPullDistance(0);
      isPullingRef.current = false;
    }
  };

  const handleTouchEnd = async () => {
    if (!isPullingRef.current && pullDistance === 0) {
      touchPullStartRef.current = null;
      return;
    }

    const PULL_THRESHOLD = 55;
    if (pullDistance >= PULL_THRESHOLD && !isPullRefreshing) {
      setIsPullRefreshing(true);
      setPullDistance(46); // Keep spinner visible during refresh
      try {
        await Promise.all([
          fetchEmails(1, false, false),
          refreshFolders(token),
        ]);
      } finally {
        setTimeout(() => {
          setIsPullRefreshing(false);
          setPullDistance(0);
        }, 300);
      }
    } else {
      setPullDistance(0);
    }

    touchPullStartRef.current = null;
    isPullingRef.current = false;
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    pageRef.current = 1;
  };

  const handleSort = (field: "date" | "from" | "subject") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setShowSortMenu(false);
  };

  const { del, patch } = useApi();
  const token = useAuthStore((s) => s.token);

  const handleBatchDelete = () => {
    const ids = Array.from(selectedEmailIds);
    const toDelete = emails.filter((e) => ids.includes(e.id));
    if (toDelete.length === 0) return;

    deleteEmailsWithUndo(
      toDelete,
      async () => {
        for (const email of toDelete) {
          try {
            await del(`/mail/mails/${encodeURIComponent(email.folderId)}/${email.id}`);
          } catch (err) {
            console.error("Error al mover correo a Papelera:", err);
          }
        }
        refreshFolders(token);
        fetchEmails(1);
      },
      { message: `${toDelete.length} correo(s) movido(s) a Papelera` }
    );
  };

  const handleBatchMarkRead = async () => {
    const ids = Array.from(selectedEmailIds);
    try {
      for (const id of ids) {
        const email = emails.find((e) => e.id === id);
        if (email) {
          await patch(`/mail/mails/${encodeURIComponent(email.folderId)}/${email.id}/flags`, {
            read: true,
          });
        }
      }
      markAsRead(ids);
      clearSelection();
      refreshFolders(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al marcar como leído");
    }
  };

  const handleBatchMarkUnread = async () => {
    const ids = Array.from(selectedEmailIds);
    try {
      for (const id of ids) {
        const email = emails.find((e) => e.id === id);
        if (email) {
          await patch(`/mail/mails/${encodeURIComponent(email.folderId)}/${email.id}/flags`, {
            read: false,
          });
        }
      }
      markAsUnread(ids);
      clearSelection();
      refreshFolders(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al marcar como no leído");
    }
  };

  const handleBatchMove = async (destFolder: string) => {
    const ids = Array.from(selectedEmailIds);
    try {
      for (const id of ids) {
        const email = emails.find((e) => e.id === id);
        if (email) {
          await patch(`/mail/mails/${encodeURIComponent(email.folderId)}/${email.id}/move`, { destFolder });
        }
      }
      removeEmails(ids);
      clearSelection();
      setShowBatchMoveMenu(false);
      refreshFolders(token);
      fetchEmails(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al mover correos");
    }
  };

  const handleBatchAddLabel = async (labelId: string) => {
    const ids = Array.from(selectedEmailIds);
    useEmailStore.getState().addLabelToEmails(ids, labelId);
    clearSelection();
    setShowBatchLabelMenu(false);
    try {
      for (const emailId of ids) {
        await apiRef.current.post(`/labels/${labelId}/emails`, { emailId });
      }
    } catch (err) {
      console.error("Failed to assign label to emails:", err);
    }
  };

  const activeFolderName = activeLabelId
    ? labels.find((l) => l.id === activeLabelId)?.name || "Etiqueta"
    : folders.find((f) => f.id === activeFolderId)?.name || "Bandeja de entrada";

  const allSelected = emails.length > 0 && selectedEmailIds.size === emails.length;

  return (
    <div className="w-full lg:w-[350px] lg:min-w-[350px] lg:max-w-[400px] h-full flex flex-col border-r border-[var(--border-color)] bg-[var(--bg-primary)] min-w-0 max-w-full overflow-x-hidden">
      {/* Header (Clean & Flat Search / Selection Bar with mobile touch optimizations) */}
      <div className="w-full px-3 sm:px-3 pt-[max(0.625rem,env(safe-area-inset-top))] sm:pt-[max(0.5rem,env(safe-area-inset-top))] pb-2.5 sm:pb-2 border-b border-[var(--border-subtle)] flex-shrink-0 box-border">
        <div className="flex items-center gap-2 sm:gap-2 w-full min-w-0">
          {/* Mobile drawer trigger */}
          {onOpenFolderDrawer && (
            <button
              onClick={onOpenFolderDrawer}
              className="lg:hidden p-2 sm:p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors flex-shrink-0 active:bg-[var(--bg-tertiary)]"
              title="Ver carpetas"
              aria-label="Abrir carpetas"
            >
              <Menu size={19} />
            </button>
          )}

          {/* Select all checkbox button */}
          <button
            onClick={allSelected ? clearSelection : selectAllEmails}
            className="p-2 sm:p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
            title={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
            aria-label={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
          >
            {allSelected ? <CheckSquare size={18} className="text-accent" /> : <Square size={18} />}
          </button>

          {/* Search Bar */}
          <div className="flex-1 relative min-w-0">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full h-9 sm:h-7 pl-9 pr-8 text-sm sm:text-xs bg-[var(--bg-secondary)] border-0 text-[var(--text-primary)] placeholder-[var(--text-muted)]/60 outline-none focus:ring-1 focus:ring-accent"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Sort / Filter Icon Button */}
          <div className="relative flex-shrink-0" ref={sortMenuRef}>
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="h-9 w-9 sm:h-7 sm:w-7 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors active:bg-[var(--bg-tertiary)]"
              title="Ordenar correos"
              aria-label="Ordenar correos"
            >
              <ArrowUpDown size={16} />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] min-w-[140px] shadow-lg">
                {[
                  { field: "date" as const, label: "Fecha" },
                  { field: "from" as const, label: "Remitente" },
                  { field: "subject" as const, label: "Asunto" },
                ].map((item) => (
                  <button
                    key={item.field}
                    onClick={() => handleSort(item.field)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-tertiary)] transition-colors ${
                      sortBy === item.field
                        ? "text-accent font-semibold"
                        : "text-[var(--text-primary)]"
                    }`}
                  >
                    {item.label}
                    {sortBy === item.field && (
                      <span className="ml-1 text-xs">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Manual Refresh Button */}
          <button
            onClick={() => fetchEmails(1)}
            className="h-9 w-9 sm:h-7 sm:w-7 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors active:bg-[var(--bg-tertiary)]"
            title="Actualizar correos"
            aria-label="Actualizar correos"
          >
            <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Batch actions */}
      {selectedEmailIds.size > 0 && (
        <div className="w-full max-w-full box-border flex items-center gap-1 px-3 py-2 bg-accent-light border-b border-[var(--border-color)] flex-wrap min-w-0">
          <span className="text-xs text-accent font-bold mr-auto truncate">
            {selectedEmailIds.size} {selectedEmailIds.size === 1 ? "seleccionado" : "seleccionados"}
          </span>
          <button
            onClick={handleBatchMarkRead}
            className="flex items-center gap-1 p-1.5 hover:bg-[var(--bg-tertiary)] transition-colors"
            title="Marcar como leídos"
            aria-label="Marcar como leídos"
          >
            <MailOpen size={14} className="text-[var(--text-secondary)]" />
            <span className="text-xs font-medium text-[var(--text-secondary)]">Leído</span>
          </button>
          <button
            onClick={handleBatchMarkUnread}
            className="flex items-center gap-1 p-1.5 hover:bg-[var(--bg-tertiary)] transition-colors"
            title="Marcar como no leídos"
            aria-label="Marcar como no leídos"
          >
            <Mail size={14} className="text-[var(--text-secondary)]" />
            <span className="text-xs font-medium text-[var(--text-secondary)]">No leído</span>
          </button>
          <div className="relative flex items-center gap-1" ref={batchMoveRef}>
            <button
              onClick={() => { setShowBatchMoveMenu(!showBatchMoveMenu); setShowBatchLabelMenu(false); }}
              className="flex items-center gap-1 p-1.5 hover:bg-[var(--bg-tertiary)] transition-colors"
              title="Mover a..."
              aria-label="Mover correos a otra carpeta"
              aria-haspopup="menu"
              aria-expanded={showBatchMoveMenu}
            >
              <FolderInput size={14} className="text-[var(--text-secondary)]" />
              <span className="text-xs font-medium text-[var(--text-secondary)]">Mover</span>
            </button>
            {showBatchMoveMenu && (
              <div role="menu" className="absolute left-0 top-full mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] min-w-[160px] max-h-64 overflow-y-auto">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    role="menuitem"
                    onClick={() => handleBatchMove(folder.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-primary)]"
                  >
                    {folder.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative flex items-center gap-1" ref={batchLabelRef}>
            <button
              onClick={() => { setShowBatchLabelMenu(!showBatchLabelMenu); setShowBatchMoveMenu(false); }}
              className="flex items-center gap-1 p-1.5 hover:bg-[var(--bg-tertiary)] transition-colors"
              title="Etiquetar"
              aria-label="Aplicar etiqueta a los correos seleccionados"
              aria-haspopup="menu"
              aria-expanded={showBatchLabelMenu}
            >
              <Tag size={14} className="text-[var(--text-secondary)]" />
              <span className="text-xs font-medium text-[var(--text-secondary)]">Etiqueta</span>
            </button>
            {showBatchLabelMenu && (
              <div role="menu" className="absolute left-0 top-full mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] min-w-[160px] max-h-64 overflow-y-auto">
                {labels.length === 0 && (
                  <p className="px-3 py-2 text-xs text-[var(--text-muted)]">Sin etiquetas</p>
                )}
                {labels.map((label) => (
                  <button
                    key={label.id}
                    role="menuitem"
                    onClick={() => handleBatchAddLabel(label.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left"
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${label.color}15`)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <span className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: label.color }} />
                    <span className="font-medium text-[var(--text-primary)]">{label.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleBatchDelete}
            className="flex items-center gap-1 p-1.5 hover:bg-[var(--bg-tertiary)] transition-colors"
            title="Eliminar"
            aria-label="Mover los correos seleccionados a Papelera"
          >
            <Trash2 size={14} className="text-red-500" />
            <span className="text-xs font-medium text-red-500">Eliminar</span>
          </button>
        </div>
      )}

      {/* Email list */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="flex-1 w-full max-w-full min-w-0 overflow-y-auto overflow-x-hidden relative overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        {/* Pull-to-refresh visual indicator */}
        {(pullDistance > 0 || isPullRefreshing) && (
          <div
            className="w-full flex items-center justify-center overflow-hidden transition-all duration-150"
            style={{
              height: `${pullDistance}px`,
              opacity: Math.min(1, pullDistance / 40),
            }}
          >
            <div className="flex items-center gap-2 text-accent text-xs font-semibold py-2">
              <RefreshCw
                size={16}
                className={isPullRefreshing ? "animate-spin" : ""}
                style={{
                  transform: isPullRefreshing ? undefined : `rotate(${pullDistance * 4}deg)`,
                }}
              />
              <span>
                {isPullRefreshing
                  ? "Actualizando bandeja..."
                  : pullDistance >= 55
                  ? "Suelta para actualizar"
                  : "Desliza para actualizar"}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-3 mt-3 px-3 py-2 text-xs border border-red-500/20 bg-red-500/10 text-red-500 font-medium">
            {error}
          </div>
        )}
        {isLoading && emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-[var(--text-muted)] p-6 select-none">
            <EmailSpinner size={40} className="mb-2" />
            <span className="text-xs">Cargando correos...</span>
          </div>
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-[var(--text-muted)] p-6 select-none">
            <EmailSpinner size={40} off className="mb-2" />
            <p className="text-xs">Sin correos</p>
          </div>
        ) : (
          <>
            {emails.map((email) => (
              <EmailListItem
                key={email.id}
                email={email}
                isSelected={currentEmail?.id === email.id}
                onOpenDraft={onOpenDraft}
              />
            ))}
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <EmailSpinner size={24} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
